import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { CustomEvents } from '@app/_helpers';
import { ConfigurationService, EventsService } from '@app/_shared/providers';
import { PLANS_DIALOG_TITLE } from '@app/_shared/strings';
import { AuthService } from '@app/modules/auth';
import { MbPlansComponent } from '@app/modules/trading/modules/mb-plans/mb-plans.component';
import { PLAN_ALERT_CONFIG } from '@app/modules/trading/modules/mb-plans/providers/mb-plans.constants';
import { CurrentPlan, PlanAlertType, UserDetails } from '@app/modules/trading/modules/mb-plans/providers/mb-plans.interface';
import { MbPlansService } from '@app/modules/trading/modules/mb-plans/providers/mb-plans.service';
import { PlanSubscription } from '@app/modules/trading/modules/profile/components/plans/plans.enums';
import * as moment from 'moment';
import { forkJoin } from 'rxjs';


@Component({
  selector: 'app-plan-alert',
  templateUrl: './plan-alert.component.html',
  styleUrls: ['./plan-alert.component.scss']
})
export class PlanAlertComponent implements OnInit {
  planInfo: CurrentPlan;
  isClosed: boolean = true;

  alertType: string;
  PlanAlertType = PlanAlertType;

  alertPlanExpiryDays: number = 0;
  extraDay: number = 1;
  isAutopayOff: boolean = false;

  @Output() alertClosed = new EventEmitter<void>();
  alertConfigs;

  constructor(private configService: ConfigurationService,
    private matDialog: MatDialog,
    private router: Router,
    public authService: AuthService,
    private eventsService: EventsService,
    private mbPlansService: MbPlansService
  ) { }

  ngOnInit(): void {
    forkJoin([
      this.mbPlansService.getCurrentPlan(),
      this.configService.getServerConfig(),
      this.mbPlansService.getUserDetails()
    ]).subscribe({
      next: ([currentPlan, serverConf, userDetails]) => {
        this.planInfo = currentPlan;
        this.isAutopayOff = this.planInfo.is_autopay_off;
        this.alertPlanExpiryDays = serverConf.plan_expiry_alert_period;
        this.showAlert(userDetails);
      },
      error: () => {
        this.isClosed = true;
        this.authService.isPlanAlert = !this.isClosed;
      }
    });
  }

  showAlert(userDetails: UserDetails) {
    this.alertConfigs = this.configService.remoteConfigCacheObj?.PLANS_CONFIG?.alertConfig ?? PLAN_ALERT_CONFIG;
    for (let alertType in this.alertConfigs) {
      const planName = alertType === PlanAlertType.PLAN_EXPIRED ? userDetails.previous_plan : this.planInfo.plan.plan_name;
      this.alertConfigs[alertType].alertText = this.alertConfigs[alertType].alertText
        .replace('{{plan_name}}', planName)
        .replace('{{billing_cycle}}', this.getCountString(this.daysToSubsEnd));
    }

    if (this.planInfo.plan.level <= 1 && moment().isBefore(moment(this.planInfo.subscription_end_date))) {
      this.isClosed = false;
      this.alertType = PlanAlertType.PLAN_ZERO;
    } else if (moment().isBetween(moment(this.planInfo.subscription_end_date).subtract(this.alertPlanExpiryDays, 'days'), this.planInfo.subscription_end_date)) {
      this.isClosed = false;
      if (this.planInfo.subscription_status === PlanSubscription.INACTIVE || this.isAutopayOff) {
        this.alertType = PlanAlertType.PLAN_VALIDITY;
      } else {
        this.alertType = PlanAlertType.PLAN_AUTORENEWAL;
      }
    } else if (moment().isAfter(moment(this.planInfo.subscription_end_date)) && this.planInfo.subscription_status === PlanSubscription.CANCELLED) {
      this.isClosed = false;
      this.alertType = this.PlanAlertType.PLAN_EXPIRED;
    } else {
      this.close();
      return;
    }

    this.authService.isPlanAlert = !this.isClosed;
    this.eventsService.publish(CustomEvents.bannerClosed);
  }

  getPlanAlertClass() {
    switch (this.alertType) {
      case PlanAlertType.PAYMENT_UNSUCCESSFUL:
      case PlanAlertType.PLAN_EXPIRED:
        return 'plan-alert-negative';
      case PlanAlertType.PLAN_AUTORENEWAL:
      case PlanAlertType.PLAN_VALIDITY:
        return 'plan-alert-warning ';
      default:
        return 'plan-alert-warning ';
    }
  }

  close() {
    this.isClosed = true;
    this.authService.isPlanAlert = !this.isClosed;
    this.alertClosed.emit();
    this.eventsService.publish(CustomEvents.bannerClosed);
  }

  upgrade(subscribe: false) {
    let data = { title: PLANS_DIALOG_TITLE };
    if (subscribe) {
      data['planId'] = this.planInfo.plan.id;
    }

    this.matDialog.open(MbPlansComponent, {
      data: data,
      width: '90%',
      panelClass: ['dialog-no-padding-rounded'],
    });
  }

  openHelp() {
    this.router.navigate(['/trading/profile/help']);
  }

  get daysToSubsEnd() {
    return moment(this.planInfo.subscription_end_date).diff(moment(), 'days');
  }

  getCountString(count: number) {
    if (count === 0) {
      return 'today';
    } else if (count === 1) {
      return 'in 1 day';
    } else {
      return `in ${count} days`;
    }
  }

}
